// Round 3 (R3-H01 / spec H3): neutral feedback identity-transition seam.
//
// CurrentUserContext calls prepareFeedbackIdentityTransition() BEFORE every
// cookie-changing persona switch / login / logout — the same pre-mutation
// pattern as prepareActivityIdentityTransition — without coupling the auth
// context to FeedbackMarkupProvider internals. The provider registers a
// handler at mount; on transition the module:
//   1. atomically advances the origin-wide identity epoch (outgoing INACTIVE)
//      and broadcasts it to every same-origin tab;
//   2. lets the provider synchronously fence autosaves/request generations and
//      persist the outgoing actor's session (meaningful → inactive snapshot
//      with the new epoch; empty → cleared);
//   3. if meaningful local-only work cannot be stored restorably, REACTIVATES
//      the current actor's epoch and reports blocked — the caller must NOT
//      proceed with the persona mutation.

import { advanceFeedbackIdentityEpoch, readFeedbackIdentityRecord } from './feedbackIdentity.js';

let transitionHandler = null;

export function registerFeedbackIdentityTransitionHandler(handler) {
  transitionHandler = handler;
  return () => {
    if (transitionHandler === handler) transitionHandler = null;
  };
}

export const FEEDBACK_TRANSITION_BLOCKED_MESSAGE =
  'Your feedback draft has work that exists only on this device and could not be stored for recovery. Save or discard the draft before switching users.';

export async function prepareFeedbackIdentityTransition() {
  const handler = transitionHandler;
  const ownerUserId =
    handler?.getOwnerUserId?.() ?? readFeedbackIdentityRecord()?.ownerUserId ?? null;
  const record = await advanceFeedbackIdentityEpoch({
    ownerUserId,
    activationState: 'inactive',
  });
  if (!handler?.onIdentityTransition) return { ok: true, record };
  let result = null;
  try {
    result = await handler.onIdentityTransition(record);
  } catch {
    // A handler fault must never strand the user mid-auth; the epoch advance
    // above already fenced stale writers, which is the safety-critical part.
    result = { ok: true };
  }
  if (result?.blocked) {
    const reactivated = await advanceFeedbackIdentityEpoch({
      ownerUserId,
      activationState: 'active',
    });
    return {
      ok: false,
      blocked: true,
      message: result.message || FEEDBACK_TRANSITION_BLOCKED_MESSAGE,
      record: reactivated,
    };
  }
  return { ok: true, record };
}
